import {
  AbortMultipartUploadCommand,
  CompleteMultipartUploadCommand,
  CreateMultipartUploadCommand,
  ListPartsCommand,
  S3Client,
  UploadPartCommand,
} from '@aws-sdk/client-s3';

import { TIMEOUT } from '@/utils/const';

import { MIN_PART_SIZE, PARALLEL } from '../../constants';
import { IStoreItem, ITokenData } from '../../type';
import { getChunk } from '../../utils';
import { IInitResponse, IUploadOptions, IUploadRetryOptions, UploaderBase } from './base';

interface IUploadPart {
  Bucket: string;
  Key: string;
  UploadId: string;
  Body: any;
  PartNumber: number;
}

type PartItem = {
  partNumber: number;
  data: any;
  lastPart?: boolean;
};

type ProgressInfo = {
  loaded: number;
  total: number;
  part: number;
  percent: number;
};

interface IConcurrentUpload {
  client: S3Client;
  dataFeeder: AsyncGenerator<PartItem>;
  uploadedPartsSet: Set<number>;
  progress?: (...args: any) => any;
  progressInfo: ProgressInfo;
  Key: string;
  bucket: string;
  UploadId: string;
}

// AWS上传
export class AwsUploader implements UploaderBase {
  constructor(private timeout: number = TIMEOUT) {}

  // 初始化
  public async init(tokenData: ITokenData): Promise<IInitResponse> {
    try {
      const { region, accessKey, accessSecret, bucket, securityToken, filePath } =
        tokenData;
      // 获取client
      const client = new S3Client({
        region,
        credentials: {
          accessKeyId: accessKey,
          secretAccessKey: accessSecret,
          sessionToken: securityToken,
        },
        // 尝试次数
        maxAttempts: 10,
      });
      // 创建初始化上传命令
      const command = new CreateMultipartUploadCommand({
        Bucket: bucket,
        Key: filePath,
      });

      const initRes = await client.send(command);
      if (!initRes) throw new Error('Get Init Info Error');

      return {
        uploadId: initRes.UploadId || '',
        client,
      };
    } catch (error) {
      console.error(error);
      return { uploadId: '', client: null };
    }
  }

  // 批量上传
  public async upload(params: IUploadOptions, retry = false) {
    const { client, file, options, uploadId, tokenData } = params;
    const { bucket, filePath } = tokenData;

    // 并发数
    const parallel = options ? options?.parallel || PARALLEL : PARALLEL;
    // 每片的大小
    const chunkSize = options ? options?.partSize || MIN_PART_SIZE : MIN_PART_SIZE;
    // 进度回调函数
    const { progress } = options;
    // 创建异步生成器asyncGenerator
    const dataFeeder = getChunk({ file, chunkSize });
    // 当前进行的上传
    const concurrentUploaders = [];
    // 已经上传部分的Index索引
    let uploadedPartsSet: Set<number> = new Set([]);
    // 进度信息
    const progressInfo: ProgressInfo = {
      // 已经上传成功的文件大小
      loaded: 0,
      // 总文件大小
      total: file?.size,
      // 当前上传的chunk part
      part: 0,
      // 已完成的进度
      percent: 0,
    };

    // 是否是重试或者断点续传
    if (retry) {
      // 获取已上传的parts
      const _uploadedParts = await this.getUploadParts(
        client as S3Client,
        uploadId,
        filePath,
        bucket,
      );
      const _uploadedIndexArr: number[] = [];
      // 获取已经上传的part number
      _uploadedParts.forEach((item) => {
        if (item.PartNumber !== undefined) {
          _uploadedIndexArr.push(item.PartNumber);
        }
      });
      uploadedPartsSet = new Set(_uploadedIndexArr);

      // 计算已上传的进度
      const loaded = _uploadedParts.reduce((acc, cur) => {
        const size = cur?.Size || 0;
        return acc + size;
      }, 0);
      progressInfo.loaded = loaded;
      const percent = Number((loaded / progressInfo.total).toFixed(2));
      progressInfo.percent = percent;
      const checkpoint = {
        Bucket: bucket,
        Key: filePath,
        uploadId: uploadId,
        ...progressInfo,
      };
      progress && progress(percent, checkpoint);
    }

    // 并发请求
    try {
      for (let index = 0; index < parallel; index++) {
        const currentUpload = this.__doConcurrentUpload({
          client: client as S3Client,
          dataFeeder,
          uploadedPartsSet,
          progress,
          progressInfo,
          Key: filePath,
          UploadId: uploadId,
          bucket: tokenData.bucket,
        });
        concurrentUploaders.push(currentUpload);
      }
      await Promise.all(concurrentUploaders);
      const parts = await this.getUploadParts(
        client as S3Client,
        uploadId,
        filePath,
        bucket,
      );
      console.log('----parts', parts);
      // 完成分片，生成location
      const res = await this.completePart(
        client as S3Client,
        uploadId,
        filePath,
        bucket,
        parts,
      );
      if (res) {
        progressInfo.percent = 1;
        const checkpoint = {
          Bucket: bucket,
          Key: filePath,
          uploadId,
          ...progressInfo,
        };
        progress(1, checkpoint);
      }
      return res;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  // 并发上传
  private async __doConcurrentUpload(params: IConcurrentUpload) {
    // 参数信息
    const {
      // s3客户端
      client,
      // 迭代器
      dataFeeder,
      // 已经上传部分的Index
      uploadedPartsSet,
      // 进度信息
      progressInfo,
      // 进度的回调，通知外部进度函数
      progress,
      Key,
      UploadId,
      bucket,
    } = params;

    // 循环迭代器，成功之后执行下一个迭代器循环
    for await (const dataPart of dataFeeder) {
      // 检查下当前进行的是不是未上传的部分
      if (!uploadedPartsSet.has(dataPart.partNumber)) {
        const params = {
          Bucket: bucket,
          Key,
          UploadId,
          Body: dataPart.data,
          PartNumber: dataPart.partNumber,
        };

        // 单片上传
        const res = await this.uploadPart(client, params);
        if (res) {
          const checkpoint = {
            Bucket: params.Bucket,
            Key: params.Key,
            uploadId: UploadId,
            ...progressInfo,
          };
          progressInfo.loaded += dataPart?.data?.size || 0;
          const percent = Number((progressInfo.loaded / progressInfo.total).toFixed(2));
          // 手动置为0.99，aws分片上传完后，还需要手动合并分片，合并完成之后再置为1
          const lastPercent = percent >= 1 ? 0.99 : percent;
          progressInfo.percent = lastPercent;
          progressInfo.part = dataPart?.partNumber;
          progress && progress(lastPercent, checkpoint);
          if (dataPart.partNumber) uploadedPartsSet.add(dataPart.partNumber);
        }
      }
    }
  }

  // 上传单片
  private async uploadPart(client: S3Client, params: IUploadPart) {
    try {
      const command = new UploadPartCommand({ ...params });
      const res = await client.send(command);
      if (res.$metadata.httpStatusCode !== 200) throw new Error('上传失败');
    } catch (error) {
      throw new Error('上传失败');
    }

    return true;
  }

  // 完成上传
  private async completePart(
    client: S3Client,
    uploadId: string,
    key: string,
    bucket: string,
    uploadedParts: any,
  ) {
    const uploadCompleteParams = {
      Bucket: bucket,
      Key: key,
      Body: undefined,
      UploadId: uploadId,
      MultipartUpload: {
        Parts: uploadedParts,
      },
    };
    const completeCommand = new CompleteMultipartUploadCommand(uploadCompleteParams);
    const res = await client.send(completeCommand);
    if (typeof res?.Location === 'string' && res.Location.includes('%2F')) {
      res.Location = res.Location.replace(/%2F/g, '/');
    }
    return res;
  }

  // 取消上传
  public async cancel(params: IStoreItem) {
    const { client: _client, uploadId, tokenData, progressData } = params;
    const { transCodeHandler } = progressData;
    const { bucket, region, accessKey, accessSecret, securityToken, filePath } =
      tokenData;
    const abortCommand = new AbortMultipartUploadCommand({
      Bucket: bucket,
      Key: filePath,
      UploadId: uploadId,
    });
    const client =
      _client ||
      new S3Client({
        region: region,
        credentials: {
          accessKeyId: accessKey,
          secretAccessKey: accessSecret,
          sessionToken: securityToken,
        },
        // 尝试次数
        maxAttempts: 10,
      });
    transCodeHandler && transCodeHandler();
    const res = await (client as S3Client).send(abortCommand);
    console.log(res, 'cancelRes');
    if (res) return true;
    return false;
  }

  // 重试
  public async retry(params: IUploadRetryOptions) {
    const { info, options } = params;

    const { tokenData, client: _client, uploadId, file } = info;

    const { region, accessKey, accessSecret, securityToken } = tokenData;
    // 拿client
    const client =
      _client ||
      new S3Client({
        region,
        credentials: {
          accessKeyId: accessKey,
          secretAccessKey: accessSecret,
          sessionToken: securityToken,
        },
        // 尝试次数
        maxAttempts: 10,
      });

    return await this.upload(
      {
        client,
        uploadId,
        file,
        filePath: '',
        options,
        tokenData,
      },
      true,
    );
  }

  // 获取已经上传的分片信息
  private async getUploadParts(
    client: S3Client,
    UploadId: string,
    Key: string,
    bucket: string,
  ) {
    const input = {
      Bucket: bucket,
      Key,
      UploadId,
    };
    const listPartsCommand = new ListPartsCommand(input);
    const res = await client.send(listPartsCommand);
    if (res) {
      const { Parts = [] } = res;
      Parts.sort((a: any, b: any) => a.PartNumber - b.PartNumber);
      return Parts;
    }
    return [];
  }
}
